import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toastError } from '../utils/toasts';
import WorkspaceFiscalYearSelector from './WorkspaceFiscalYearSelector';
import fullLogo from '../assets/full_logo.png';
import {
  IconGauge, IconShoppingCart, IconUsers, IconFileText, IconFileInvoice,
  IconArrowBackUp, IconCreditCard, IconShoppingBag, IconTruck, IconReceipt,
  IconPercentage, IconWallet, IconCash, IconBuildingBank, IconScale,
  IconBook, IconBook2, IconHandClick, IconFileEuro, IconSitemap,
  IconBriefcase, IconChecklist, IconRefresh, IconUserShield, IconSettings,
  IconBuilding, IconHeadset, IconChevronRight, IconFileExport, IconLogout,
  IconCalendar, IconMessage, IconHistory,
} from '@tabler/icons-react';

const ICON_MAP = {
  'gauge':          IconGauge,
  'shopping-cart':  IconShoppingCart,
  'users':          IconUsers,
  'file-text':      IconFileText,
  'file-invoice':   IconFileInvoice,
  'arrow-back-up':  IconArrowBackUp,
  'credit-card':    IconCreditCard,
  'shopping-bag':   IconShoppingBag,
  'truck':          IconTruck,
  'receipt':        IconReceipt,
  'percentage':     IconPercentage,
  'wallet':         IconWallet,
  'cash':           IconCash,
  'building-bank':  IconBuildingBank,
  'scale':          IconScale,
  'book':           IconBook,
  'book-open':      IconBook2,
  'hand-click':     IconHandClick,
  'file-euro':      IconFileEuro,
  'sitemap':        IconSitemap,
  'briefcase':      IconBriefcase,
  'checklist':      IconChecklist,
  'refresh':        IconRefresh,
  'user-shield':    IconUserShield,
  'settings':       IconSettings,
  'building':       IconBuilding,
  'headset':        IconHeadset,
  'chevron-right':  IconChevronRight,
  'file-export':    IconFileExport,
  'logout':         IconLogout,
  'calendar':       IconCalendar,
  'message':        IconMessage,
  'history':        IconHistory,
};

function NavIcon({ name, size = 18 }) {
  const Component = ICON_MAP[name];
  if (!Component) return <span style={{ width: 18 }} />;
  return <Component size={size} strokeWidth={1.6} />;
}

export default function Sidebar({ onNavClick = () => {} }) {
  const location = useLocation();
  const {
    currentUser, isAdmin, isCollaborator, isSuperAdmin,
    tenantType, logout, accountingEnabled, stockEnabled, isCabinet, tenantInfo,
    eSignatureEnabled, cabinetManagementEnabled,
  } = useAuth();
  const { workspaces, activeWorkspace, switchWorkspace, loading: workspaceLoading, workspaceAccountingEnabled, workspaceStockEnabled, workspaceESignatureEnabled, workspaceCabinetManagementEnabled } = useWorkspace();

  const [tejLoading, setTejLoading]     = useState(false);
  const [openFlyoutKey, setOpenFlyoutKey] = useState(null);
  const [flyoutTopPx, setFlyoutTopPx]   = useState(0);

  const sidebarRef = useRef(null);
  const flyoutRef  = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      const inSidebar = sidebarRef.current?.contains(e.target);
      const inFlyout  = flyoutRef.current?.contains(e.target);
      if (!inSidebar && !inFlyout) setOpenFlyoutKey(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Pour les cabinets (comptable et médical), les features viennent du workspace ; pour les companies, du tenant
  const effectiveAccountingEnabled = isCabinet ? workspaceAccountingEnabled : accountingEnabled;
  const effectiveStockEnabled = isCabinet ? workspaceStockEnabled : stockEnabled;
  const effectiveESignatureEnabled = isCabinet ? workspaceESignatureEnabled : eSignatureEnabled;
  const effectiveCabinetManagementEnabled = isCabinet ? workspaceCabinetManagementEnabled : cabinetManagementEnabled;
  // TEJ a son propre entitlement, indépendant de la signature électronique
  const tejEnabled = !!tenantInfo?.entitlements?.tej?.enabled;

  const baseMenuItems = useMemo(() => {
    const items = [
      { path: '/dashboard', label: 'Tableau de bord', icon: 'gauge', tourKey: 'nav-dashboard' },
      {
        key: 'ventes',
        label: 'Ventes',
        icon: 'shopping-cart',
        childrenTourKeys: { clients: 'nav-clients', quotes: null, invoices: 'nav-invoices', 'credit-notes': null, payments: 'nav-payments' },
        children: tenantType === 'cabinet' || tenantType === 'cabinet_medical'
          ? [
              { path: '/clients',      label: 'Clients',      icon: 'users' },
              { path: '/quotes',       label: 'Devis',        icon: 'file-text' },
              { path: '/invoices',     label: tenantType === 'cabinet_medical' ? 'Notes d\'honoraires' : 'Factures', icon: 'file-invoice' },
              { path: '/credit-notes', label: 'Avoirs',       icon: 'arrow-back-up' },
              { path: '/payments',     label: 'Règlements',   icon: 'credit-card' },
            ]
          : [
              { path: '/clients',      label: 'Clients',          icon: 'users' },
              { path: '/quotes',       label: 'Devis',            icon: 'file-text' },
              { path: '/invoices',     label: 'Factures émises',  icon: 'file-invoice' },
              { path: '/credit-notes', label: 'Avoirs',           icon: 'arrow-back-up' },
              { path: '/payments',     label: 'Règlements reçus', icon: 'credit-card' },
            ],
      },
      {
        key: 'achats',
        label: 'Achats',
        icon: 'shopping-bag',
        children: tenantType === 'cabinet' || tenantType === 'cabinet_medical'
          ? [
              { path: '/suppliers',             label: 'Fournisseurs',       icon: 'truck' },
              { path: '/purchase-invoices',     label: 'Factures',           icon: 'file-invoice' },
              { path: '/purchase-payments',     label: 'Règlements',         icon: 'receipt' },
              { path: '/purchase-payments/rs',  label: 'Retenues RS achats', icon: 'percentage' },
            ]
          : [
              { path: '/suppliers',             label: 'Fournisseurs',       icon: 'truck' },
              { path: '/purchase-invoices',     label: 'Factures reçues',    icon: 'file-invoice' },
              { path: '/purchase-payments',     label: 'Règlements',         icon: 'receipt' },
              { path: '/purchase-payments/rs',  label: 'Retenues RS achats', icon: 'percentage' },
            ],
      },
      {
        key: 'tresorerie',
        label: 'Trésorerie',
        icon: 'wallet',
        children: [
          { path: '/treasury/cash', label: 'Caisse',       icon: 'cash' },
          { path: '/treasury/bank', label: 'Banque',       icon: 'building-bank' },
          { path: '/treasury/rs',   label: 'Retenues RS',  icon: 'percentage' },
        ],
      },
    ];

    if (effectiveStockEnabled) {
      items.push({
        key: 'stock',
        label: 'Stock',
        icon: 'building-bank',
        children: [
          { path: '/stock',                  label: 'Tableau de bord', icon: 'gauge' },
          { path: '/stock/items',            label: 'Articles',        icon: 'shopping-cart' },
          { path: '/stock/inventory',        label: 'État du stock',   icon: 'checklist' },
          { path: '/stock/movements',        label: 'Mouvements',      icon: 'arrow-back-up' },
          { path: '/stock/categories',       label: 'Catégories',      icon: 'sitemap' },
          { path: '/stock/settings',         label: 'Paramètres',      icon: 'settings' },
        ],
      });
    }

    if (effectiveAccountingEnabled) {
      items.push({
        key: 'compta',
        label: 'Comptabilité',
        icon: 'scale',
        children: [
          { path: '/accounting',                  label: 'Tableau de bord',    icon: 'dashboard' },
          { path: '/accounting/entries',          label: 'Écritures auto.',    icon: 'book' },
          { path: '/accounting/manual-entry',     label: 'Saisie manuelle',    icon: 'pencil' },
          { path: '/accounting/journal',          label: 'Journal',            icon: 'book-open' },
          { path: '/accounting/general-ledger',   label: 'Grand Livre',        icon: 'list' },
          { path: '/accounting/trial-balance',    label: 'Balance',            icon: 'scale' },
          { path: '/accounting/financial-statements', label: 'États Financiers',icon: 'report-analytics' },
          { path: '/accounting/ar',               label: 'Postes clients',     icon: 'hand-click' },
          { path: '/accounting/ap',               label: 'Postes fournisseurs',icon: 'file-euro' },
          { path: '/accounting/chart-of-accounts',label: 'Plan Comptable',     icon: 'sitemap' },
          { path: '/accounting/lettrage',         label: 'Lettrage',           icon: 'hand-click' },
          { path: '/accounting/annexes',          label: 'Annexes',            icon: 'clipboard-list' },
          { path: '/accounting/settings',         label: 'Paramètres',         icon: 'settings' },
        ],
      });
    }

    if (tenantType === 'cabinet') {
      if (effectiveCabinetManagementEnabled) {
        items.push({
          key: 'gestion-cabinet',
          label: 'Gestion Cabinet',
          icon: 'briefcase',
          children: [
            { path: '/tasks',           label: 'Tâches',            icon: 'checklist' },
            { path: '/recurring-tasks', label: 'Tâches récurrentes',icon: 'refresh' },
          ],
        });
      }
    } else if (tenantType === 'cabinet_medical') {
      // Aucune rubrique Tâches pour les cabinets médicaux
    } else {
      items.push({ path: '/tasks', label: 'Tâches', icon: 'checklist' });
    }

    return items;
  }, [tenantType, effectiveAccountingEnabled, effectiveStockEnabled, effectiveCabinetManagementEnabled]);

  const adminMenuItems = useMemo(() => [
    { path: '/users',        label: 'Utilisateurs',          icon: 'user-shield' },
    { path: '/settings',     label: 'Paramètres',            icon: 'settings' },
    { path: '/fiscal-years', label: 'Exercices comptables',  icon: 'calendar' },
    { path: '/audit-logs',   label: "Journal d'audit",       icon: 'history' },
    { path: '/sms',          label: 'Campagne WhatsApp',     icon: 'message' },
  ], []);

  const superAdminMenuItems = useMemo(() => [
    { path: '/admin/tenants', label: 'Cabinets',        icon: 'building' },
    { path: '/admin/support', label: 'Support Clients', icon: 'headset' },
  ], []);

  const handleLogout = () => { logout(); window.location.href = '/'; };

  const getUserInfo = () => {
    if (isSuperAdmin)    return { name: 'Super Admin Plateforme', badge: 'SUPER ADMIN', badgeColor: 'bg-purple-600' };
    if (isAdmin())       return { name: 'Administrateur',          badge: 'ADMIN',       badgeColor: 'bg-red-500' };
    if (isCollaborator()) return { name: currentUser?.name || 'Collaborateur', badge: 'COLLAB', badgeColor: 'bg-blue-500' };
    return { name: currentUser?.name || 'Utilisateur', badge: 'USER', badgeColor: 'bg-gray-500' };
  };

  const userInfo = getUserInfo();

  const handleOpenTej = async () => {
    try {
      setTejLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { toastError('Token introuvable.'); return; }
      const workspaceId = activeWorkspace?._id;
      if (!workspaceId) {
        toastError("Veuillez d'abord sélectionner un dossier client dans la liste.");
        setTejLoading(false);
        return;
      }
      const resp = await fetch('/api/sso/tej', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) { toastError(data?.message || 'Erreur SSO TEJ'); return; }
      if (!data?.url) { toastError('Réponse SSO invalide'); return; }
      window.location.href = `${data.url}&workspaceId=${workspaceId}`;
    } catch (e) {
      toastError(e?.message || 'Erreur SSO TEJ');
    } finally {
      setTejLoading(false);
    }
  };

  const renderLinkItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <li key={item.path} className="px-3" data-tour={item.tourKey}>
        <Link
          to={item.path}
          onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
          className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
            isActive
              ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md border-l-4 border-blue-500'
              : 'text-gray-300 hover:text-white hover:bg-gray-700'
          }`}
        >
          <NavIcon name={item.icon} />
          <span>{item.label}</span>
        </Link>
      </li>
    );
  };

  const openedGroup = baseMenuItems.find((x) => x.key && x.key === openFlyoutKey) || null;

  return (
    <nav
      ref={sidebarRef}
      className="w-72 bg-gray-800 text-white h-screen fixed z-50 flex flex-col shadow-2xl relative"
    >
      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #4B5563; border-radius: 3px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #6B7280; }
        .sidebar-scroll { scrollbar-width: thin; scrollbar-color: #4B5563 transparent; }
      `}</style>

      {/* HEADER LOGO */}
      <div className="p-4 flex flex-col items-center justify-center border-b border-gray-700 bg-gray-900/50 flex-shrink-0">
        <div className="bg-white p-2 rounded-lg w-full flex justify-center mb-2 shadow-sm">
          <img src={fullLogo} alt="AccountInTime" className="h-10 w-auto object-contain" />
        </div>
        <p className="text-gray-400 text-xs mt-1 uppercase tracking-widest font-semibold">
          {isSuperAdmin ? 'Admin Plateforme' : tenantType === 'cabinet' ? 'Espace Cabinet comptable' : tenantType === 'cabinet_medical' ? 'Espace Cabinet médical' : 'Espace Entreprise'}
        </p>
      </div>

      {/* INFO UTILISATEUR */}
      <div className="p-4 bg-gray-700/50 mx-3 my-4 rounded-lg text-sm border border-gray-600/50 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <strong className="block text-gray-100">{userInfo.name}</strong>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold text-white tracking-wider ${userInfo.badgeColor}`}>
              {userInfo.badge}
            </span>
          </div>
          <div className="h-2 w-2 bg-green-500 rounded-full shadow-[0_0_5px_2px_rgba(34,197,94,0.4)]" />
        </div>
      </div>

              {/* SÉLECTEURS DE CONTEXTE */}
      {!isSuperAdmin && (
        <div className="px-3 mb-4 flex-shrink-0" data-tour="workspace-selector">
          <WorkspaceFiscalYearSelector />
        </div>
      )}

      {/* NAVIGATION */}
      <ul className="flex-1 py-2 space-y-1 overflow-y-auto sidebar-scroll pb-6">
        {isSuperAdmin ? (
          <>
            {superAdminMenuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path} className="px-3">
                  <Link
                    to={item.path}
                    onClick={() => onNavClick()}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-purple-800 to-purple-900 text-white shadow-md border-l-4 border-purple-500'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <NavIcon name={item.icon} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </>
        ) : (
          <>
            {baseMenuItems.map((item) => {
              if (!item.children) return renderLinkItem(item);
              const isGroupActive = item.children.some((c) => location.pathname === c.path);
              const isOpen = openFlyoutKey === item.key;
              const tourAttr = item.key === 'ventes' ? 'nav-sales' : item.key === 'achats' ? 'nav-purchases' : item.key === 'tresorerie' ? 'nav-treasury' : item.key === 'compta' ? 'nav-accounting' : item.key === 'stock' ? 'nav-stock' : null;
              return (
                <li key={item.key} className="px-3" data-tour={tourAttr}>
                  <button
                    type="button"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setFlyoutTopPx(rect.top);
                      setOpenFlyoutKey((k) => (k === item.key ? null : item.key));
                    }}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
                      isGroupActive || isOpen
                        ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md border-l-4 border-blue-500'
                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <NavIcon name={item.icon} />
                      <span>{item.label}</span>
                    </span>
                    <IconChevronRight
                      size={14}
                      strokeWidth={2}
                      className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <ul className="md:hidden ml-4 mt-1 space-y-1 border-l-2 border-gray-700 pl-2">
                      {item.children.map((child) => {
                        const isChildActive = location.pathname === child.path;
                        const childTour = item.key === 'ventes' && child.path === '/clients' ? 'nav-clients'
                          : item.key === 'ventes' && child.path === '/invoices' ? 'nav-invoices'
                          : item.key === 'ventes' && child.path === '/payments' ? 'nav-payments'
                          : null;
                        return (
                          <li key={child.path} data-tour={childTour}>
                            <Link
                              to={child.path}
                              onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                isChildActive
                                  ? 'text-white bg-gray-700'
                                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
                              }`}
                            >
                              <NavIcon name={child.icon} />
                              <span>{child.label}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}

            {isAdmin() && (
              <>
                <li className="px-3 mt-4 mb-2">
                  <div className="border-t border-gray-700 pt-4">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest px-3 mb-2">
                      Administration
                    </p>
                  </div>
                </li>
                {adminMenuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const tourAttr = item.path === '/settings' ? 'nav-settings' : null;
                  return (
                    <li key={item.path} className="px-3" data-tour={tourAttr}>
                      <Link
                        to={item.path}
                        onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
                        className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md border-l-4 border-red-500'
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                        }`}
                      >
                        <NavIcon name={item.icon} />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </>
            )}
          </>
        )}
      </ul>

      {/* FLYOUT (desktop only) */}
      {!!openedGroup && (
        <div
          ref={flyoutRef}
          className="hidden md:block absolute left-full z-50 w-64 bg-gray-900 border border-gray-700 shadow-2xl rounded-xl"
          style={{ top: flyoutTopPx }}
        >
          <div className="p-2 max-h-80 overflow-y-auto">
            <ul className="space-y-1">
              {openedGroup.children.map((child) => {
                const isActive = location.pathname === child.path;
                return (
                  <li key={child.path}>
                    <Link
                      to={child.path}
                      onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        isActive
                          ? 'bg-gray-800 text-white border-l-4 border-blue-500'
                          : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <NavIcon name={child.icon} />
                      <span>{child.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="mt-auto bg-gray-900/50 border-t border-gray-700 p-4 space-y-2 flex-shrink-0">
        {tejEnabled && (
          <button
            onClick={handleOpenTej}
            disabled={tejLoading}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-green-400 hover:text-white hover:bg-green-600/20 border border-green-600/30 w-full text-left transition-all duration-200 disabled:opacity-50"
          >
            <NavIcon name="file-export" />
            <span>{tejLoading ? 'Ouverture...' : 'Accès TEJ Direct'}</span>
          </button>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:text-white hover:bg-red-600/20 border border-red-600/30 w-full text-left transition-all duration-200"
        >
          <NavIcon name="logout" />
          <span>Déconnexion</span>
        </button>
      </div>
    </nav>
  );
}
